'use client';

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { cx } from './cx';

export type KListRowTone = 'danger' | 'accent';

type RowProps = {
  /** Глиф плитки слева (наша подсистема значков, 16) или картинка на всю плитку. */
  icon?: ReactNode;
  /** Цвет плитки ролью палитры (`var(--green)`, `var(--tg-blue)`…): пастель и глиф
   *  берут его через currentColor. Без цвета — приглушённые чернила. */
  iconTint?: string;
  title: ReactNode;
  /** Значение справа (серым, одной строкой с многоточием). */
  value?: ReactNode;
  /** Подстрока под словом (серым). */
  hint?: ReactNode;
  /** Третья строка под подстрокой цветом роли (QUICK-START FIX-3 §7.8, зеркало `note` строки `nav` приложения):
   *  «Есть незавершённые анкеты» акцентом. */
  note?: ReactNode;
  noteTone?: 'accent';
  /** Шеврон «›» справа — строка-переход. */
  chevron?: boolean;
  /** Тон слова: `danger` — красное («Выйти»), `accent` — акцентное («Добавить…»). */
  tone?: KListRowTone;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  /** Строка-ссылка: рисуется `<a>`. */
  href?: string;
  /** У строки-ссылки: `_blank` — новая вкладка (тогда `rel` — `noopener`). */
  target?: string;
  rel?: string;
  /** В `value` стоит КОНТРОЛ (переключатель), а не слово: значение не обрезается и не
   *  сжимается, своей строки-обёртки у контрола нет. */
  control?: boolean;
  className?: string;
};

const chev = (
  <svg className="k-lrow__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 5 7 7-7 7" />
  </svg>
);

/** Строка списка набора — `.k-lrow` в kit.css (родной список iOS `AccountFieldRow`, Э4.9):
 *  плитка значка, слово, подстрока, значение справа, шеврон. С `href` — ссылка, с
 *  `onClick` — кнопка, иначе — просто строка. Строки кладутся в `KListGroup`: волосок
 *  между ними рисует CSS. */
export function KListRow({ icon, iconTint, title, value, hint, note, noteTone, chevron, tone, disabled, onClick, href, target, rel, control, className }: RowProps) {
  const cls = cx(
    'k-lrow',
    icon != null && 'k-lrow--ico',
    hint != null && hint !== '' && 'k-lrow--has-hint',
    tone && `k-lrow--${tone}`,
    control && 'k-lrow--ctl',
    className,
  );
  const tileStyle: CSSProperties | undefined = iconTint ? { color: iconTint } : undefined;
  const body = (
    <>
      {icon != null ? (
        <span className="k-lrow__ico" style={tileStyle} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="k-lrow__main">
        <span className="k-lrow__t">{title}</span>
        {hint != null && hint !== '' ? <span className="k-lrow__hint">{hint}</span> : null}
        {note != null && note !== '' ? <span className={cx('k-lrow__note', noteTone && `k-lrow__note--${noteTone}`)}>{note}</span> : null}
      </span>
      {value != null && value !== '' ? <span className="k-lrow__val">{value}</span> : null}
      {chevron ? chev : null}
    </>
  );
  if (href != null) {
    return (
      <a
        className={cls}
        href={disabled ? undefined : href}
        target={target}
        rel={rel}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
      >
        {body}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cls} disabled={disabled} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={cls} aria-disabled={disabled || undefined}>{body}</div>;
}

/** Группа строк — карточка `--surf` радиусом `--bub-r` (insetGrouped iOS). */
export function KListGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('k-lgroup', className)}>{children}</div>;
}

/**
 * РАЗДЕЛ СПИСКА: подпись над группой строк, сама группа и сноска под ней — как у родных
 * списков iOS (подпись раздела и `footer` у insetGrouped). Подняты из окна бота в набор
 * (владелец 01.10.2026: «это общие стили для всех шторок»). Числа — кадр iOS-приложения:
 * подпись и сноска 13/17, сноска светлее подписи; до следующего раздела 12 (со сноской 10).
 */
export function KListSection({
  label,
  hint,
  children,
  className,
}: {
  /** Подпись раздела над группой (серым). */
  label?: ReactNode;
  /** Сноска под группой (третичным серым). */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const hasHint = hint != null && hint !== '';
  return (
    <div className={cx('k-lsec', hasHint && 'k-lsec--foot', className)}>
      {label != null && label !== '' ? <div className="k-lsec__cap">{label}</div> : null}
      {children}
      {hasHint ? <p className="k-lsec__foot">{hint}</p> : null}
    </div>
  );
}

/** Отдельная серая фраза между разделами списка («Запусков пока нет»). */
/** Подпись под группой. `tone: 'warn'` — янтарная плашка-предупреждение (админка: «по данным от …», когда живая
 *  проверка не успела и показана копия — ADM-PLATFORMS-FIX-1 К7). */
export function KListNote({ children, tone }: { children: ReactNode; tone?: 'warn' }) {
  return <p className={tone === 'warn' ? 'k-lnote k-lnote--warn' : 'k-lnote'}>{children}</p>;
}

/** Галочка выбранной строки — в `value` строки-выбора (акцентом, как в листах выбора iOS).
 *  Рисунок — пропорции SF `checkmark` (почти квадрат, `WizMarks.check`: 17 semibold), а не
 *  плоская «птичка»; на телефоне в браузере размер 22 даёт kit.css (кадры B10/B11, видимая
 *  галочка ≈ 15 × 14 pt). */
export function KListCheck() {
  return (
    <svg className="k-lrow__check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4.5 12.5 9.5 17.5 19.5 5.5" />
    </svg>
  );
}

/** Значение строки со значком перед словом (значок сети у имени аккаунта). `tint` — цвет
 *  значка: чужой фирменный цвет сети приходит данными, ролей палитры у него нет. */
export function KListValueIcon({ icon, tint, children }: { icon: ReactNode; tint?: string; children: ReactNode }) {
  return (
    <span className="k-lrow__val-ico">
      <span className="k-lrow__val-ico-i" style={tint ? { color: tint } : undefined}>
        {icon}
      </span>
      {children}
    </span>
  );
}

/** Блок между разделами списка: кнопка во всю ширину, переключатель периода. */
export function KListBlock({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('k-lblock', className)}>{children}</div>;
}

/** Поле ввода прямо внутри группы строк (правится на месте, без перехода). */
export function KListField({ children }: { children: ReactNode }) {
  return <div className="k-lfield">{children}</div>;
}

/** Короткая форма под списком листа: поле (или несколько) и ряд кнопок справа —
 *  «Добавить папку», «Добавить метку». `flush` — форма стоит первой в листе (без отступа
 *  сверху): лист правки одного слова. */
export function KListForm({ children, actions, flush }: { children: ReactNode; actions?: ReactNode; flush?: boolean }) {
  return (
    <div className={cx('k-lform', flush && 'k-lform--flush')}>
      {children}
      {actions ? <div className="k-lform__acts">{actions}</div> : null}
    </div>
  );
}
